"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { FaCreditCard } from "react-icons/fa6";
import { FaBitcoin } from "react-icons/fa6";
import { MdHistory } from "react-icons/md";
import { BsFileEarmarkRuled } from "react-icons/bs";
import { PiStrategy } from "react-icons/pi";
import { GiExplosiveMaterials } from "react-icons/gi";
import { MdOutlineTipsAndUpdates } from "react-icons/md";
import { FaNutritionix } from "react-icons/fa";
import { RiMentalHealthLine } from "react-icons/ri";
import { MdOutlinePersonalInjury } from "react-icons/md";
import Ende from "./end";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import PaymentCrypto from "@/components/login-order/cryptoOrderHomePAge";
import PaymentCard from "@/components/login-order/cardOrderHomePage";
import useFetcherUser from "@/components/checkout/FtecherUser";
import { useSession } from "next-auth/react";
import RedirectToCard from "@/components/login-order/loginRedirectToCard";
import RedirectToCrypto from "@/components/login-order/loginRedirectToCrypto";
import { RiCloseLargeFill } from "react-icons/ri";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { RiDoubleQuotesL } from "react-icons/ri";
import { RiDoubleQuotesR } from "react-icons/ri";
import { useLocale } from "next-intl";
import ExportPublicFolder from "@/components/login-order/downloadPackHomePage";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

type FileType = "pdf" | "jpg" | "png" | "txt" | "doc" | "docx" | "epub";

interface PackFile {
  name: string;
  path: string;
  type: FileType;
}

interface Props {
  bgSrc: string;
  bookSrc: string;
  packFiles: PackFile[];
  packFolderName: string;
}

// ─────────────────────────────────────────────
// Skeleton shimmer for buttons
// ─────────────────────────────────────────────

const ButtonSkeleton = () => (
  <div className="flex flex-col items-center justify-center gap-4 pb-4 xl:pb-6 animate-pulse">
    <div className="h-14 w-80 md:w-[28rem] lg:w-[32rem] rounded-xl bg-white/15" />
    <div className="h-14 w-80 md:w-[28rem] lg:w-[32rem] rounded-xl bg-white/10" />
  </div>
);

// ─────────────────────────────────────────────
// PaymentButtons
// ─────────────────────────────────────────────

interface PaymentButtonsProps {
  status: string;
  userinfo: any;
  isLoading: boolean;
  packFiles: PackFile[];
  packFolderName: string;
  locale: string;
  openCard: () => void;
  openCrypto: () => void;
  th: any;
}

const PaymentButtons = ({
  status,
  userinfo,
  isLoading,
  packFiles,
  packFolderName,
  locale,
  openCard,
  openCrypto,
  th,
}: PaymentButtonsProps) => {
  if (isLoading) return <ButtonSkeleton />;

  if (status === "authenticated") {
    if (!userinfo?.is_payed) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 pb-4 xl:pb-6">
          <PaymentCard
            customData={{ productType: "ebook", source: "homepage" }}
            className="text-center text-secondary flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
          >
            {th("buy-card-button")}
          </PaymentCard>
          <PaymentCrypto />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-6 xl:gap-8 pb-4 xl:pb-6">
        <p className="text-b text-sm capitalize max-w-md">
          {th("download-button-guide")}
        </p>
        <ExportPublicFolder
          locale={locale || "en"}
          files={packFiles}
          folderName={packFolderName}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 pb-4 xl:pb-6">
      <button
        className="text-center text-secondary flex justify-center gap-2 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
        onClick={openCard}
      >
        <FaCreditCard size={24} />
        <p className="m-0 text-xs md:text-sm lg:text-base font-bold">
          {th("buy-card-button")}
        </p>
      </button>

      <button
        className="text-center text-secondary flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
        onClick={openCrypto}
      >
        <FaBitcoin size={26} />
        <p className="m-0 text-xs md:text-sm lg:text-base font-bold">
          {th("buy-crypto-button")}
        </p>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function FullscreenSlider({ bgSrc, bookSrc, packFiles, packFolderName }: Props) {
  const t = useTranslations("tophero");
  const th = useTranslations("Padel-Master-The-Game-Home-Page");
  const te = useTranslations("Hero");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const searchParams = useSearchParams();
  const query = searchParams.get("affiliate");
  const { userinfo } = useFetcherUser();
  const { data: session, status } = useSession();

  const [isAuthOpenCard, setIsAuthOpenCard] = useState(false);
  const [isAuthOpenCrypto, setIsAuthOpenCrypto] = useState(false);
  const [authCard, setAuthCard] = useState<"login" | "signup">("login");
  const [authCrypto, setAuthCrypto] = useState<"login" | "signup">("login");

  const isPaymentLoading =
    status === "loading" ||
    (status === "authenticated" && userinfo === undefined);

  const openCard = () => {
    setAuthCard("login");
    setIsAuthOpenCard(true);
  };

  const openCrypto = () => {
    setAuthCrypto("login");
    setIsAuthOpenCrypto(true);
  };

  useEffect(() => {
    if (!query) return;
    localStorage.setItem("affiliate", query);

    const sendClickData = async () => {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_URL}api/clicks/`,
          {
            method: "POST",
            headers: {
              Authorization: `Token ${process.env.NEXT_PUBLIC_TOKEN}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ affiliate_code: query }),
          }
        );
        await response.json();
      } catch (error) {
        console.error("Error sending click data:", error);
      }
    };

    sendClickData();
  }, [query]);

  const features: Feature[] = [
    { icon: <MdHistory className="w-6 h-6" />, title: th("t1"), description: th("p1") },
    { icon: <BsFileEarmarkRuled className="w-6 h-6" />, title: th("t2"), description: th("p2") },
    { icon: <PiStrategy className="w-6 h-6" />, title: th("t3"), description: th("p3") },
    { icon: <GiExplosiveMaterials className="w-6 h-6" />, title: th("t4"), description: th("p4") },
    { icon: <MdOutlineTipsAndUpdates className="w-6 h-6" />, title: th("t5"), description: th("p5") },
    { icon: <FaNutritionix className="w-6 h-6" />, title: th("t6"), description: th("p6") },
    { icon: <RiMentalHealthLine className="w-6 h-6" />, title: th("t7"), description: th("p7") },
    { icon: <MdOutlinePersonalInjury className="w-6 h-6" />, title: th("t8"), description: th("p8") },
  ];

  return (
    <>
      <style>{`
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0px rgba(255,255,255,0.45); }
          100% { box-shadow: 0 0 0 18px rgba(255,255,255,0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fadeSlideUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .btn-card { animation: pulse-ring 2s ease-out infinite; }
        .btn-crypto { animation: pulse-ring 2s ease-out infinite 1s; }
        .book-float { animation: float 4s ease-in-out infinite; }
        .fade-up-1 { animation: fadeSlideUp 0.8s ease-out 0.2s both; }
        .fade-up-2 { animation: fadeSlideUp 0.8s ease-out 0.5s both; }
        .fade-up-3 { animation: fadeSlideUp 0.8s ease-out 0.8s both; }
        .pay-btn {
          border: 4px solid white;
          color: white;
          border-radius: 9999px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(8px);
          transition: all 0.3s ease-in-out;
          cursor: pointer;
        }
        .pay-btn:hover {
          background: white;
          color: black;
          transform: scale(1.12);
          box-shadow: 0 0 35px rgba(255,255,255,0.65);
        }
        .pay-btn:active { transform: scale(0.95); }
        .pay-btn:hover svg { color: black; }
      `}</style>

      {/*
        ── Page background — injected directly onto <html> via a style tag ──
        WHY NOT `fixed`: on mobile Safari/Chrome the address bar collapses and
        expands as you scroll, which changes the visual viewport in real time.
        Any element with `position:fixed` is sized against the *layout*
        viewport (the larger, stable value). During the address-bar animation
        those two values diverge, leaving a visible gap (white bar) at the
        bottom before the browser reconciles them.

        THE RELIABLE FIX: set the background directly on the <html> element.
        The browser always stretches the root element to fill its own chrome —
        including the area behind the address bar — so it can never show a gap.
        `background-attachment: fixed` gives the parallax-style "stays in
        place while scrolling" effect without any DOM element needing to cover
        the viewport.
      */}
      <style>{`
        html {
          background-image: url('${bgSrc}');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          background-color: #000;
        }
        html::after {
          content: '';
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.55);
          pointer-events: none;
          z-index: -1;
        }
        body {
          background: transparent !important;
        }
      `}</style>

      <div className="relative w-full overflow-hidden bg-transparent">

        {/* ════════════════════════════════════════
            MOBILE (< lg)
        ════════════════════════════════════════ */}
        <div className="flex flex-col lg:hidden px-5 pb-12 select-none">
          <div className="h-20 sm:h-24" />

          <div className="fade-up-3 flex flex-col items-center gap-2">
            <div className="relative w-full h-96 md:h-[28rem] lg:h-[32rem] book-float">
              <Image
                src={bookSrc}
                alt={te("padel-book-alt")}
                quality={100}
                fill
                sizes="100vw"
                className="object-contain"
                priority
              />
            </div>

            <PaymentButtons
              status={status}
              userinfo={userinfo}
              isLoading={isPaymentLoading}
              packFiles={packFiles}
              packFolderName={packFolderName}
              locale={locale}
              openCard={openCard}
              openCrypto={openCrypto}
              th={th}
            />
          </div>

          <div className="mt-14 sm:mt-16 md:mt-20" />

          <div className="pb-4">
            <h2 className="fade-up-1 text-sm sm:text-lg md:text-xl font-bold text-b uppercase mb-1 tracking-wide">
              {th("title")}
            </h2>
            <h1 className="fade-up-2 text-4xl sm:text-5xl md:text-6xl font-extrabold text-white uppercase leading-tight">
              {th("title1")} <br /> {th("title2")}
            </h1>
          </div>
        </div>

        {/* ════════════════════════════════════════
            DESKTOP (>= lg)
        ════════════════════════════════════════ */}
        <div
          className="hidden lg:grid grid-cols-2 gap-8 items-end select-none
            px-10 xl:px-16 2xl:px-24
            pb-12 xl:pb-16 2xl:pb-20"
        >
          <div className="col-span-2 h-24 xl:h-28 2xl:h-32" />

          {/* Left — Text */}
          <div className="flex flex-col justify-end pb-4 xl:pb-6 2xl:pb-8">
            <h2 className="fade-up-1 text-xl xl:text-2xl 2xl:text-3xl font-semibold text-b uppercase tracking-wide">
              {th("title")}
            </h2>
            <h1 className="fade-up-2 text-4xl xl:text-6xl 2xl:text-7xl font-extrabold text-white uppercase leading-none">
              {th("title1")} <br /> {th("title2")}
            </h1>
          </div>

          {/* Right — Book + Buttons */}
          <div className="fade-up-3 flex flex-col items-center">
            <div className="relative w-full h-[420px] xl:h-[520px] 2xl:h-[620px] book-float mr-16">
              <Image
                src={bookSrc}
                alt={te("padel-book-alt")}
                quality={100}
                fill
                sizes="50vw"
                className="object-contain"
                priority
              />
            </div>

            <PaymentButtons
              status={status}
              userinfo={userinfo}
              isLoading={isPaymentLoading}
              packFiles={packFiles}
              packFolderName={packFolderName}
              locale={locale}
              openCard={openCard}
              openCrypto={openCrypto}
              th={th}
            />
          </div>
        </div>

        {/* ════════════════════════════════════════
            FEATURES GRID
        ════════════════════════════════════════ */}
        <div className="mx-3 lg:mx-24 py-32 select-none" id="content">
          <div className="items-end mb-20 space-y-2">
            <h2 className="fade-up-2 text-xl xl:text-2xl 2xl:text-3xl font-extrabold text-b uppercase leading-none">
              {th("title3")}
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl uppercase text-white">
              {th("sub-title3")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col p-6 border rounded-xl bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm"
              >
                <div className="text-b mb-4 border border-b w-fit p-3 rounded-full">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 flex items-center uppercase gap-2 text-b">
                  {feature.title}
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ════════════════════════════════════════
            PACK / DEVICE MOCKUP + COMPARISON
        ════════════════════════════════════════ */}
        <div
          className="py-3 px-4 relative z-10 border bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm select-none"
          id="pack"
        >
          <div className="container mx-auto xl:px-6 grid grid-cols-1 2xl:grid-cols-2 gap-2 items-center py-16 md:py-0">
            <div className="h-[350px] md:h-[850px] w-full relative">
              <Image
                src="/Device-Mockup.avif"
                alt="device mockup"
                quality={85}
                fill
                sizes="(max-width: 1023px) 100vw, 50vw"
                className="object-cover"
                priority
              />
            </div>

            <div className="w-full select-none">
              <div className="p-6 mb-20 space-y-4 mx-auto text-white">
                <h2 className="fade-up-2 text-xl xl:text-2xl 2xl:text-3xl font-extrabold text-b uppercase leading-none">
                  {th("title4")}
                </h2>
                <p className="text-gray-300 md:text-lg">{th("pt4-1")}</p>
                <p className="text-gray-300 md:text-lg">{th("pt4-2")}</p>
                <p className="text-gray-300 md:text-lg">{th("pt4-3")}</p>
                <p className="text-lg uppercase font-bold text-a">{th("pt4-4")}</p>
              </div>
            </div>
          </div>

          {/* ── Comparison Table ── */}
          <div className="container mx-auto px-6 pb-20 pt-10 select-none">
            <h2 className="fade-up-2 text-2xl xl:text-3xl font-extrabold text-b uppercase leading-none mb-10 text-center">
              {th("title5")}
            </h2>

            <div className="overflow-x-auto rounded-2xl mx-auto max-w-5xl border border-white/20">
              <table
                className={`w-full ${isArabic ? "text-right" : "text-left"} text-white border-collapse`}
                dir={isArabic ? "rtl" : "ltr"}
              >
                <thead>
                  <tr className="bg-white/10 backdrop-blur-sm">
                    <th className={`px-6 py-5 text-xs md:text-sm lg:text-base font-bold uppercase tracking-wider text-gray-300 w-1/3 ${isArabic ? "text-right" : "text-left"}`}>
                      {th("Feature")}
                    </th>
                    <th className={`px-6 py-5 text-xs md:text-sm lg:text-base font-bold uppercase tracking-wider ${isArabic ? "text-right" : "text-left"}`}>
                      <span className={`inline-flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                        {th("Official-Platform")}
                      </span>
                    </th>
                    <th className={`px-6 py-5 text-xs md:text-sm lg:text-base font-bold uppercase tracking-wider flex flex-wrap items-baseline gap-1 ${isArabic ? "justify-end" : ""}`}>
                      <span className={`inline-flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                        {th("Marketplaces")}
                      </span>
                      <div className="text-xs font-normal text-gray-400 normal-case tracking-normal mt-0.5">
                        Amazon, Apple, ...
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feature: th("Lifetime-updates"),
                      official: { label: th("Yes"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                      market: { label: th("No"), icon: <RiCloseLargeFill className="text-red-600" size={20} />, positive: false },
                    },
                    {
                      feature: th("Languages-included"),
                      official: { label: th("languages"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                      market: { label: th("language-only"), icon: <RiCloseLargeFill className="text-red-600" size={20} />, positive: false },
                    },
                    {
                      feature: th("Direct-support"),
                      official: { label: th("Priority-support"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                      market: { label: th("Limited"), icon: "⚠️", positive: false },
                    },
                    {
                      feature: th("Instant-access"),
                      official: { label: th("Yes"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                      market: { label: th("Yes"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                    },
                    {
                      feature: th("Affiliate-program"),
                      official: { label: th("Earn"), icon: <IoCheckmarkDoneSharp className="text-green-500" size={24} />, positive: true },
                      market: { label: th("No-only"), icon: <RiCloseLargeFill className="text-red-600" size={20} />, positive: false },
                    },
                  ].map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t border-white/10 transition-colors duration-200 hover:bg-white/5 ${i % 2 === 0 ? "bg-black/20" : "bg-black/10"}`}
                    >
                      <td className="px-6 py-5 font-semibold text-gray-200 text-xs md:text-sm lg:text-base">
                        {row.feature}
                      </td>
                      <td className="px-6 py-5">
                        <span className={`inline-flex gap-2 text-xs md:text-sm lg:text-base font-light items-center ${isArabic ? "flex-row-reverse" : ""}`}>
                          <span>{row.official.icon}</span> {row.official.label}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`inline-flex gap-2 text-xs md:text-sm lg:text-base font-light items-center ${isArabic ? "flex-row-reverse" : ""}`}>
                          <span>{row.market.icon}</span> {row.market.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Market logos ── */}
          <div className="container mx-auto px-6 pb-20 pt-10 select-none">
            <div className="flex flex-wrap gap-x-6 justify-center items-center">
              {[
                { src: "/markets/Barnes.svg", width: 180, tooltip: th("Barnes-tool") },
                { src: "/markets/Amazon.svg", width: 120, tooltip: th("Amazon-tool") },
                { src: "/markets/Hoopla.svg", width: 120, tooltip: th("Hoopla-tool"), extraClass: "opacity-20" },
                { src: "/markets/Apple.svg", width: 80, tooltip: th("Apple-tool") },
              ].map(({ src, width, tooltip, extraClass = "" }) => (
                <div key={src} className="relative group flex flex-col items-center">
                  <Image
                    src={src}
                    alt="logo"
                    width={width}
                    height={40}
                    className={`grayscale ${extraClass} hover:grayscale-0 hover:opacity-100 transition-all duration-300`}
                  />
                  <div className="absolute bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {tooltip}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-x-6 justify-center items-center">
              {[
                { src: "/markets/OverDrive.svg", width: 160, tooltip: th("OverDrive-tool") },
                { src: "/markets/Indiebound.svg", width: 120, tooltip: th("Indiebound-tool") },
                { src: "/markets/Vivlio.svg", width: 80, tooltip: th("Vivlio-tool") },
                { src: "/markets/Rakuten.svg", width: 180, tooltip: th("Rakuten-tool") },
              ].map(({ src, width, tooltip }) => (
                <div key={src} className="relative group flex flex-col items-center">
                  <Image
                    src={src}
                    alt="logo"
                    width={width}
                    height={40}
                    className="grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                  />
                  <div className="absolute bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {tooltip}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex mt-12 lg:mt-4 justify-center select-none">
              <p className="text-gray-300 text-lg flex gap-1">
                <RiDoubleQuotesL className="text-gray-600" />
                {th("sub")}
                <RiDoubleQuotesR className="text-gray-600" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <Ende />

      <RedirectToCard
        isOpen={isAuthOpenCard}
        onClose={() => setIsAuthOpenCard(false)}
        initialMode={authCard}
      />
      <RedirectToCrypto
        isOpen={isAuthOpenCrypto}
        onClose={() => setIsAuthOpenCrypto(false)}
        initialMode={authCrypto}
      />
    </>
  );
}